import _ from 'underscore';
import queryString from 'query-string';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import type {
  IHistory, ILocation, IQueryParams, IProcessor, ScopeExtraData, ScopeParameter,
} from '../types';

export const $current = '$current';

export type ISaveScope = (
  queryParam: QueryParam,
  pathname?: string,
  extraData?: ScopeExtraData,
) => boolean | void;

function filterByKeys(params: IQueryParams, allowedKeys: string[]): IQueryParams {
  return _.isEmpty(allowedKeys) ? params : (_.pick(params, allowedKeys) as IQueryParams);
}

export class QueryParam {
  allowedKeys: string[];

  history: IHistory;

  location: ILocation;

  prevState?: IQueryParams;

  processor: IProcessor;

  saveScope: ISaveScope;

  values: IQueryParams;

  static get Empty(): QueryParam {
    return new QueryParam(
      {} as ILocation,
      { saveScope: _.noop } as unknown as IHistory,
    );
  }

  constructor(
    location: ILocation,
    history: IHistory,
    saveScope?: ISaveScope,
    processor: IProcessor = _.identity as IProcessor,
    allowedKeys: string[] = [],
  ) {
    this.processor = processor;
    this.saveScope = saveScope;
    this.allowedKeys = allowedKeys;
    this.location = location;
    this.history = history;
    this.parse();
    this.saveState();
  }

  append = (params: IQueryParams): void => {
    const allowedParams = filterByKeys(params, this.allowedKeys);

    this.values = _.defaults(
      _.omit(allowedParams, (val: ScopeParameter) => val === $current),
      this.values,
    );
  };

  remove = (keys: string | string[]): void => {
    const removeKeys = _.isArray(keys) ? keys : [keys];
    const filteredKeys = _.isEmpty(this.allowedKeys)
      ? keys : _.intersection(_.flatten(removeKeys), this.allowedKeys);

    this.values = _.omit(this.values, filteredKeys);
  };

  set = (params: IQueryParams): void => {
    this.values = this.getMerged(params);
  };

  getMerged = (params: IQueryParams): IQueryParams => {
    const allowedParams = filterByKeys(params, this.allowedKeys);
    const newParams = _.mapObject(allowedParams, (val: ScopeParameter, key: string) => (val === $current
      ? _.result(this.values, key, '') : val));
    const unmanagedParams = _.isEmpty(this.allowedKeys)
      ? {} : _.omit(this.values, this.allowedKeys);

    return _.extend(unmanagedParams, newParams);
  };

  parse = (query = this.location.search): void => {
    this.values = this.processor(queryString.parse(query));
  };

  stringify = (values: IQueryParams = this.values): string => queryString.stringify(values, { sort: false });

  isEqual = (search: string): boolean => _.isEqual(queryString.parse(this.stringify()), queryString.parse(search));

  save = (pathname?: string, extraData?: ScopeExtraData): boolean => {
    const resolvedPathname = pathname ?? this.location.pathname;

    if (this.saveScope(this, resolvedPathname, extraData)) {
      this.saveState();
      return true;
    }

    this.restoreState();
    return false;
  };

  saveState = (): void => {
    this.prevState = cloneDeep(this.values);
  };

  restoreState = (): void => {
    if (this.prevState) {
      this.values = this.prevState;
    }
  };
}
